// Copyright (c) StrangeBee 2023

import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import THTable from '@Molecules/THTable/THTable';
import { Space } from 'antd';
import React, { ReactElement, useMemo } from 'react';
import { FilterInput, SharedOrgOutput, InputShare, ProfileOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import useListQuery from '@Hooks/useListQuery';
import { computeListSelector } from '@Organisms/Profile/selector';
import client from '@Client';
import { THForm } from '@Molecules/THForm/THForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';

import './EntitySharingList.scss';

export interface SharingEventList {
    onList: (id: string, filters?: FilterInput[]) => Promise<SharedOrgOutput[]>;
    onCaseShareList?: (id: string, filters?: FilterInput[]) => Promise<SharedOrgOutput[]>;
    onAdd: (shares: InputShare[] | string[], id: string) => void;
    onBulkEdit?: (shares: InputShare[] | string[], id: string) => void;
    onDelete: (organisations: string[], id: string) => void;
    onEdit?: (profile: string, shareId: string) => Promise<void>;
}

interface EntitySharingListProps {
    caseId: string;
    entityId: string;
    eventList: SharingEventList;
    shareList: SharedOrgOutput[];
    className?: string;
    extraAction?: (organisationName: string) => ReactElement;
    hasPermissions?: boolean;
    hideOwner?: boolean;
    canEditProfile?: boolean;
}

export const EntitySharingList = ({
    entityId,
    eventList,
    shareList,
    className,
    extraAction,
    hasPermissions,
    hideOwner = false,
    canEditProfile = false,
}: EntitySharingListProps): ReactElement => {
    const i = useLocalMessage();

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const orgProfileList = useMemo(() => profileList.filter((profile) => profile.forOrg), [profileList]);

    const columns = columnsRenderer<SharedOrgOutput>([
        {
            className: 'entity-sharing-list__columns-owner custom-table__cell',
            hidden: hideOwner,
            render: ({ owner }) => (
                <THTooltip title={i('sharing.owner')}>
                    <div>{owner && <ICONS.StarIcon />}</div>
                </THTooltip>
            ),
        },
        {
            title: <ColumnTitle>{i('sharing.table.organisation')}</ColumnTitle>,
            className: 'entity-sharing-list__columns-organisation custom-table__cell',
            render: ({ organisationName }) => <>{organisationName}</>,
        },
        {
            title: <ColumnTitle>{i('sharing.table.profile')}</ColumnTitle>,
            className: 'entity-sharing-list__columns-profile custom-table__cell',
            render: ({ _id, profileName }) =>
                canEditProfile ? (
                    <THForm.Item name={['profile', _id]} className="entity-sharing-list__profile-select">
                        <THSelect
                            options={orgProfileList.map(({ name: value }) => ({
                                label: value,
                                value,
                            }))}
                        />
                    </THForm.Item>
                ) : (
                    <>{profileName}</>
                ),
        },
        {
            title: <ColumnTitle>{i('sharing.table.shared-at')}</ColumnTitle>,
            className: 'entity-sharing-list__columns-date custom-table__cell',
            render: ({ _createdAt }) => <FormatDate date={_createdAt} defaultStyle={false} />,
        },
        {
            className: 'entity-sharing-list__columns-actions custom-table__cell',
            render: ({ owner, organisationName }) => (
                <Space className="entity-sharing-list__columns-actions__space">
                    {hasPermissions && (
                        <>
                            {extraAction?.(organisationName)}

                            {!owner && (
                                <DeleteButton
                                    disabled={owner}
                                    label={i('sharing.table.action.delete')}
                                    onConfirm={(): void => eventList.onDelete([organisationName], entityId)}
                                />
                            )}
                        </>
                    )}
                </Space>
            ),
        },
    ]);

    return (
        <div className={classList('entity-sharing-list', className)}>
            {!!shareList?.length && <THTable dataSource={shareList} rowKey="_id" columns={columns} />}
        </div>
    );
};
